import { createTheme, type Theme } from '@mui/material/styles';

/**
 * Confirmation and error dialogs use a nested intent theme so the header
 * and primary actions keep the warning, error, success, or info color.
 * That theme is light-only. In dark mode, keep the colored header and
 * take the dialog body from the active scheme.
 */
export function adaptIntentDialogTheme(
  intentTheme: Theme,
  parentTheme: Theme
): Theme {
  if (parentTheme.palette.mode !== 'dark') {
    return intentTheme;
  }

  const paper = parentTheme.palette.background.paper;
  const textPrimary = parentTheme.palette.text.primary;
  const borderMain =
    parentTheme.palette.border.main ?? parentTheme.palette.divider;
  const pageSurfaces = [
    parentTheme.palette.background.default,
    parentTheme.palette.canvas?.light
  ];
  const titleBlendsWithPage = surfaceMatches(
    dialogTitleBackground(intentTheme),
    ...pageSurfaces
  );
  const primaryBlendsWithPage = surfaceMatches(
    intentTheme.palette.primary.main,
    ...pageSurfaces
  );

  return createTheme(intentTheme, {
    palette: {
      mode: 'dark',
      ...(primaryBlendsWithPage
        ? { primary: parentTheme.palette.primary }
        : {}),
      background: {
        default: parentTheme.palette.background.default,
        paper
      },
      text: {
        primary: textPrimary,
        secondary: parentTheme.palette.text.secondary
      },
      divider: parentTheme.palette.divider,
      action: parentTheme.palette.action,
      border: parentTheme.palette.border,
      canvas: parentTheme.palette.canvas
    },
    typography: {
      h1: { color: textPrimary },
      h2: { color: textPrimary },
      h3: { color: textPrimary },
      h4: { color: textPrimary }
    },
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${borderMain}`
          },
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.72)'
            }
          }
        }
      },
      ...(titleBlendsWithPage
        ? {
            MuiDialogTitle: {
              styleOverrides: {
                root: {
                  background: parentTheme.palette.action.hover,
                  color: textPrimary,
                  borderBottom: `1px solid ${borderMain}`
                }
              }
            }
          }
        : {}),
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: paper,
            backgroundImage: 'none',
            color: textPrimary
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            background: parentTheme.palette.action.hover
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          dividers: {
            borderTop: `1px solid ${borderMain}`,
            borderBottom: `1px solid ${borderMain}`
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: textPrimary
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            // The intent theme paints contained secondary buttons with the
            // light gray fill. On a dark dialog that fill is the bright spot.
            '.MuiDialog-root &.MuiButton-contained.MuiButton-colorSecondary': {
              backgroundColor: paper,
              backgroundImage: 'none',
              color: textPrimary,
              border: `1px solid ${borderMain}`,
              boxShadow: `0 1px 0 0 ${borderMain}`,
              '&:hover': {
                backgroundColor: parentTheme.palette.action.hover,
                backgroundImage: 'none'
              }
            },
            // Neutral intent primary is the page navy, so the submit button
            // has no edge. Use the active scheme's primary instead.
            ...(primaryBlendsWithPage
              ? {
                  '.MuiDialog-root &.MuiButton-contained.MuiButton-colorPrimary':
                    {
                      backgroundColor: parentTheme.palette.primary.main,
                      backgroundImage: 'none',
                      color: parentTheme.palette.primary.contrastText,
                      '&:hover': {
                        backgroundColor:
                          parentTheme.palette.primary.dark ??
                          parentTheme.palette.primary.main,
                        backgroundImage: 'none'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: parentTheme.palette.primary.main,
                        color: parentTheme.palette.primary.contrastText
                      }
                    }
                }
              : {})
          },
          outlined: {
            backgroundColor: paper,
            border: `1px solid ${borderMain}`,
            boxShadow: `0 1px 0 0 ${borderMain}`
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            // Neutral selected items use the navy primary. On a dark menu that
            // text is the same color as the page.
            '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible':
              {
                backgroundColor:
                  parentTheme.palette.action.selected ??
                  parentTheme.palette.action.hover,
                color: textPrimary
              }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: paper,
            color: textPrimary
          },
          notchedOutline: {
            borderColor: borderMain
          }
        }
      }
    }
  });
}

function dialogTitleBackground(theme: Theme): unknown {
  const root = theme.components?.MuiDialogTitle?.styleOverrides?.root;

  if (root == null || typeof root !== 'object' || Array.isArray(root)) {
    return undefined;
  }

  return 'background' in root ? root.background : undefined;
}

/** Neutral intent headers use the dark canvas color, which disappears in dark mode. */
function surfaceMatches(
  color: unknown,
  ...surfaces: Array<string | undefined>
): boolean {
  if (typeof color !== 'string') {
    return false;
  }

  const normalized = color.trim().toLowerCase();

  return surfaces.some(
    (surface) =>
      typeof surface === 'string' && surface.trim().toLowerCase() === normalized
  );
}
